/**
 * PrefetchLink - Link component with route prefetching
 * Automatically prefetches route components on hover/focus for better performance
 */

import { Link, LinkProps, NavLink, NavLinkProps } from 'react-router-dom';
import { useRoutePrefetch } from '@hooks/useRoutePrefetch';
import styled from 'styled-components';

// ============================================================================
// Types
// ============================================================================

interface PrefetchLinkProps extends LinkProps {
  prefetch?: boolean;
  children: React.ReactNode;
}

interface PrefetchNavLinkProps extends NavLinkProps {
  prefetch?: boolean;
  children: React.ReactNode;
}

// ============================================================================
// Styled Components
// ============================================================================

const sharedLinkStyles = `
  /* Inherits all Link styles and adds prefetching behavior */
  transition: all var(--transition-normal);

  &:hover {

  }

  &:focus-visible {
    outline: 3px solid var(--brand-600);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const StyledLink = styled(Link)`
  ${sharedLinkStyles}
`;

const StyledNavLink = styled(NavLink)`
  ${sharedLinkStyles}
`;

// ============================================================================
// PrefetchLink Component
// ============================================================================

export const PrefetchLink: React.FC<PrefetchLinkProps> = ({
  to,
  prefetch = true,
  children,
  ...props
}) => {
  const { prefetchOnHover, prefetchOnFocus, cancelPrefetch } =
    useRoutePrefetch();

  const handleMouseEnter = () => {
    if (prefetch && typeof to === 'string') {
      prefetchOnHover(to);
    }
  };

  const handleMouseLeave = () => {
    if (prefetch) {
      cancelPrefetch();
    }
  };

  const handleFocus = () => {
    if (prefetch && typeof to === 'string') {
      prefetchOnFocus(to);
    }
  };

  return (
    <StyledLink
      to={to}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      {...props}
    >
      {children}
    </StyledLink>
  );
};

export const PrefetchNavLink: React.FC<PrefetchNavLinkProps> = ({
  to,
  prefetch = true,
  children,
  ...props
}) => {
  const { prefetchOnHover, prefetchOnFocus, cancelPrefetch } =
    useRoutePrefetch();

  const handleMouseEnter = () => {
    if (prefetch && typeof to === 'string') {
      prefetchOnHover(to);
    }
  };

  const handleMouseLeave = () => {
    if (prefetch) {
      cancelPrefetch();
    }
  };

  const handleFocus = () => {
    if (prefetch && typeof to === 'string') {
      prefetchOnFocus(to);
    }
  };

  return (
    <StyledNavLink
      to={to}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      {...props}
    >
      {children}
    </StyledNavLink>
  );
};

export default PrefetchLink;
